import { ProductDurationType } from "@fitness-app/data-models/entities/Product";
import { type ProductClient } from "@fitness-app/data-models/entities/ProductClient";

/**
 * When does the client lose access to the product?
 *
 * Scheduled periods are the LATER end of the two, not a short circuit
 * (TRE-1725): they used to win outright, so extending the plan's own horizon
 * was invisible to anyone with a next package queued up — the change was saved,
 * the confirmation went out, and the date on screen never moved. Both sides are
 * horizons of the same access; the answer is the further one.
 */
export const getProductClientAccessEnd = (productClient: ProductClient): number | null => {
  if (productClient.paymentType === "free") {
    return null;
  }

  const scheduledEnd = productClient.scheduledAccessPeriod?.length
    ? Math.max(...productClient.scheduledAccessPeriod.map((phase) => phase.end_date))
    : null;

  const laterOf = (ownEnd: number | null): number | null =>
    scheduledEnd && ownEnd ? Math.max(scheduledEnd, ownEnd) : (ownEnd ?? scheduledEnd);

  if (productClient.paymentType === "recurring") {
    if (productClient.accessPeriod?.customDuration && productClient.accessPeriod.endDate) {
      return laterOf(productClient.accessPeriod.endDate);
    }
    if (productClient.accessPeriod?.pauseCollection?.resumes_at && productClient.accessPeriod.installmentEndsAt) {
      return laterOf(productClient.accessPeriod.pauseCollection.resumes_at);
    }

    if (productClient.accessPeriod?.endDate || productClient.accessPeriod?.installmentEndsAt) {
      return laterOf(productClient.accessPeriod.endDate || productClient.accessPeriod.installmentEndsAt || null);
    }

    return laterOf(productClient.accessPeriod?.currentPeriodEnd || null);
  }

  if (productClient.accessPeriod?.currentPeriodEnd) {
    return laterOf(productClient.accessPeriod.currentPeriodEnd);
  }

  if (productClient.productDuration?.startAt) {
    if (
      (productClient.productDuration.type === ProductDurationType.LastsFor ||
        productClient.productDuration.type === ProductDurationType.UntilDate) &&
      productClient.productDuration.endAt
    ) {
      return laterOf(productClient.productDuration.endAt);
    }
    return laterOf(null);
  }

  return laterOf(null);
};
